import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/layouts/MainLayout';
import { PageHeader, Container } from '@/components/ui';
import { CargoList, CreateCargoModal } from '@/components/cargoList';
import { useCargoListStore } from '@/store/cargoListStore';

import type { CargoItem } from '@/types/cargoList.types';

export function CargoListPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const navigate = useNavigate();

  const {
    items,
    loading,
    error,
    refreshing,
    usingMockData,
    filters,
    fetchCargoList,
    refreshCargoList,
    updateFilters,
  } = useCargoListStore();

  // Fetch cargo list on mount
  useEffect(() => {
    if (items.length === 0 && !loading) {
      fetchCargoList();
    }
  }, [items.length, loading, fetchCargoList]);

  const handleCargoClick = (item: CargoItem) => {
    navigate(`/cargo-list/${item.id}`);
  };

  const handleAddNew = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateSuccess = () => {
    refreshCargoList();
  };

  return (
    <MainLayout>
      <Container>
        <div className="space-y-6">
          {/* Page Header with Actions */}
          <PageHeader
            title="Cargo List"
            actions={
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleAddNew}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add New
                </button>

                <button
                  type="button"
                  onClick={refreshCargoList}
                  disabled={loading || refreshing}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-300 dark:hover:bg-dark-700"
                >
                  <svg
                    className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            }
          />

          {/* Cargo List Component */}
          <CargoList
            items={items}
            filters={filters}
            loading={loading}
            error={error}
            refreshing={refreshing}
            usingMockData={usingMockData}
            onFiltersChange={updateFilters}
            onRefresh={refreshCargoList}
            onCargoClick={handleCargoClick}
            onAddNew={handleAddNew}
          />

          {/* Create Cargo Modal - mounted only while open, so its reference-data
              fetches do not run on every visit to the list */}
          {isCreateModalOpen && (
            <CreateCargoModal
              isOpen={isCreateModalOpen}
              onClose={() => setIsCreateModalOpen(false)}
              onSuccess={handleCreateSuccess}
            />
          )}
        </div>
      </Container>
    </MainLayout>
  );
}
